'use client';

import {
  createContext,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';

/**
 * Tracks every currently-open edge-panel-style overlay — `Drawer`, `Modal`
 * (including its `presentation: 'drawer'` variant, since both go through the
 * same `<ModalProvider>` slot), and the app-chrome `SidePanelHost` (Alerts /
 * Help / Columns) — in open order, so one opened on top of another can tell
 * which of them is topmost. Nothing about z-index answers this on its own:
 * `Drawer` and `Modal` intentionally share the same `--z-modal`/
 * `--z-modal-backdrop` tier (see each module's own comment), so without this
 * stack whichever mounted later just happens to paint on top while the
 * earlier one keeps its own scrim, focus trap, and Esc listener live
 * underneath — double scrim, two things fighting over Tab/Esc, and (the bug
 * that motivated this) a single Escape press closing BOTH a confirm dialog
 * and the record-edit Drawer it was raised from, discarding unsaved work.
 *
 * One instance is provided once near the app root (`DrawerStackProvider` in
 * `src/app/providers.tsx`, deliberately ABOVE `ConfirmProvider`/
 * `ModalProvider` — see that file's comment) and shared by every overlay
 * below it. An overlay rendered with no provider ancestor (isolated unit
 * tests, mainly) falls back to the default instance below — since a test
 * like that only ever opens one overlay at a time, it is always alone in the
 * stack and therefore always topmost, which is exactly pre-stacking
 * behavior.
 *
 * Kept as one name-compatible file/class rather than splitting into a
 * per-component-family mechanism: every participant wants the identical
 * behavior (stay mounted while hidden, gate Esc/Tab-trap on topmost, replay
 * its entrance animation on reveal), so a second parallel stack would just
 * be this one, renamed.
 */
interface StackEntry {
  id: string;
  hasBackdrop: boolean;
  coversPanel: boolean;
  /** Set by edge panels that take part in nested stacking — see `stackedWidth`. */
  stacking?: StackingPanel;
}

/** An edge panel's own width and edge, as `stackedWidth` needs them. */
export interface StackingPanel {
  /** The panel's own CSS width (`640px`, `50%`, `100vw` for a fullscreen one). */
  width: string;
  side: 'left' | 'right';
}

class DrawerStack {
  private entries: StackEntry[] = [];
  private readonly listeners = new Set<() => void>();

  push(id: string, hasBackdrop: boolean, coversPanel: boolean, stacking?: StackingPanel) {
    if (this.entries.some((entry) => entry.id === id)) return;
    this.entries = [...this.entries, { id, hasBackdrop, coversPanel, stacking }];
    this.notify();
  }

  /**
   * Nested edge panels (Vaul's "nested drawers" pattern): a panel opened on
   * top of another on the same edge is narrower than the one beneath it by
   * `--drawer-stack-offset`, so the lower panel's edge stays in view behind
   * the top one's scrim — the reader can see there is a panel to go back to,
   * and a click on that strip closes only the top one. Returns the CSS width
   * this panel should render at: its own width, capped at the panel
   * beneath's EFFECTIVE width minus the offset (so a third level steps in
   * again from the second, not from the first). A panel with nothing beneath
   * it on its edge keeps its own width.
   */
  /**
   * True when a stacking panel on the same edge is open above `id` — i.e.
   * `id` is showing behind a nested Drawer. The panel beneath then drops to
   * the backdrop layer so the top panel's scrim dims its visible strip and a
   * click there closes the top one (see `.stacked` in `drawer.module.css`).
   */
  isStackedUnder(id: string): boolean {
    const index = this.entries.findIndex((entry) => entry.id === id);
    const own = this.entries[index]?.stacking;
    if (!own) return false;
    return this.entries
      .slice(index + 1)
      .some((entry) => entry.stacking && entry.stacking.side === own.side);
  }

  stackedWidth(id: string): string | null {
    const index = this.entries.findIndex((entry) => entry.id === id);
    const own = this.entries[index]?.stacking;
    if (!own) return null;
    for (let below = index - 1; below >= 0; below -= 1) {
      const lower = this.entries[below].stacking;
      if (!lower || lower.side !== own.side) continue;
      const lowerWidth = this.stackedWidth(this.entries[below].id) ?? lower.width;
      return `min(${own.width}, calc(${lowerWidth} - var(--drawer-stack-offset, 10px)))`;
    }
    return own.width;
  }

  pop(id: string) {
    if (!this.entries.some((entry) => entry.id === id)) return;
    this.entries = this.entries.filter((entry) => entry.id !== id);
    this.notify();
  }

  /** Vacuously true when the stack is empty — lets a not-yet-registered overlay render as topmost. */
  isTopmost(id: string): boolean {
    return this.entries.length === 0 || this.entries[this.entries.length - 1].id === id;
  }

  /**
   * True when `id` is covered by an overlay that paints its OWN full-viewport
   * backdrop (another `Drawer`, or a `Modal`) — safe to fully hide (backdrop
   * and all), since the one on top already dims/occupies the whole screen on
   * its own.
   *
   * False when the covering overlay has no backdrop of its own (the
   * `SidePanelHost` aside — Alerts/Help/Columns — which slides in over part
   * of the screen with no scrim): the covered overlay's OWN backdrop must
   * keep dimming the page, or the portion of the screen the side panel
   * doesn't cover would suddenly un-dim and become clickable while the side
   * panel is open. Only the covered overlay's PANEL (already visually
   * occluded by the opaque side panel anyway) hides in that case — see each
   * consumer's own render for how it applies this distinction.
   */
  isFullyCovered(id: string): boolean {
    if (this.isTopmost(id) || this.entries.length === 0) return false;
    return this.entries[this.entries.length - 1].hasBackdrop;
  }

  /**
   * True when `id`'s own PANEL (its actual content, not its backdrop) should
   * disappear — distinct from `isFullyCovered`, which only ever asks about
   * the covered overlay's own backdrop. A confirm dialog (`Modal`) raised
   * from inside a Drawer is `hasBackdrop: true` (same as `isFullyCovered`
   * above), but is a small centered card, not a full replacement of the
   * view — the Drawer underneath should stay visible, dimmed by the
   * confirm's own backdrop, exactly like a normal "dialog floats over a
   * dimmed form" pattern. A second Drawer opened on top, by contrast, DOES
   * want the first one's panel gone: its own content occupies the same
   * edge-to-edge space, so leaving the first panel visible underneath is
   * redundant at best and visually clashing at worst. (`Drawer` itself no
   * longer asks for this: nested Drawers step in instead — see
   * `stackedWidth` — so the one beneath stays visible.) `coversPanel` is what
   * each overlay type declares about itself for this purpose — `Drawer`
   * defaults to `true`; `Modal` and the `SidePanelHost` aside (a narrow
   * Columns/Alerts/Help panel that should open ON TOP of a Drawer, MTFM-606)
   * opt out with `false`.
   */
  isPanelHidden(id: string): boolean {
    if (this.isTopmost(id) || this.entries.length === 0) return false;
    return this.entries[this.entries.length - 1].coversPanel;
  }

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  private notify() {
    this.listeners.forEach((listener) => listener());
  }
}

const DrawerStackContext = createContext<DrawerStack>(new DrawerStack());

export function DrawerStackProvider({ children }: Readonly<{ children: ReactNode }>) {
  const [stack] = useState(() => new DrawerStack());
  return <DrawerStackContext.Provider value={stack}>{children}</DrawerStackContext.Provider>;
}

export interface OverlayStackOptions {
  /**
   * Whether this overlay paints its own full-viewport backdrop. `Drawer` and
   * `Modal` (both variants) are `true` — a covered instance of either can be
   * hidden completely. The `SidePanelHost` aside is `false` — it has no
   * backdrop of its own, so an overlay it covers must keep its OWN backdrop
   * visible. @default true
   */
  hasBackdrop?: boolean;
  /**
   * Whether THIS overlay, when it covers something else, should force that
   * other overlay's PANEL to disappear — see `DrawerStack.isPanelHidden`'s
   * own comment. `Drawer` (edge-to-edge content, own default) wants this;
   * `Modal` and `SidePanelHost` opt out with `false`, since a confirm dialog
   * or a narrow Columns panel should float over what raised it, not blank
   * it out.
   * @default true
   */
  coversPanel?: boolean;
  /**
   * The panel's own width and edge, for an edge panel that takes part in
   * nested stacking (`Drawer`) — see `DrawerStack.stackedWidth`. Omitted by
   * overlays that neither step in nor make the next one step in (`Modal`,
   * `SidePanelHost`).
   */
  stacking?: StackingPanel;
}

export interface OverlayStackPosition {
  /** Whether this is the frontmost open overlay — gate Esc/Tab-trap on this, regardless of `hasBackdrop`. */
  isTopmost: boolean;
  /** Whether this overlay's backdrop specifically should hide — see `DrawerStack.isFullyCovered`'s own comment. */
  isFullyCovered: boolean;
  /** Whether THIS overlay's own panel should hide — see `DrawerStack.isPanelHidden`'s own comment. */
  isPanelHidden: boolean;
  /** The width to render at once nested — see `DrawerStack.stackedWidth`. `null` without `stacking`. */
  stackedWidth: string | null;
  /** Showing behind a nested Drawer on the same edge — see `DrawerStack.isStackedUnder`. */
  isStackedUnder: boolean;
}

/**
 * Registers an overlay instance (`Drawer`, `Modal`, or `SidePanelHost`) with
 * the nearest stack for as long as `open` is true, and reports its position.
 * Un-registers on close or unmount.
 *
 * `useLayoutEffect`, not `useEffect`, for the push/pop: it runs before the
 * browser paints, so an overlay that opens on top of an already-open one is
 * registered — and the covered one already knows it's no longer topmost —
 * before the first paint, not one frame after.
 */
export function useOverlayStackPosition(
  open: boolean,
  { hasBackdrop = true, coversPanel = true, stacking }: OverlayStackOptions = {},
): OverlayStackPosition {
  const stack = useContext(DrawerStackContext);
  const id = useId();
  // Primitives, so an inline `stacking` object doesn't re-register every render.
  const stackingWidth = stacking?.width;
  const stackingSide = stacking?.side;

  useLayoutEffect(() => {
    if (!open) return;
    stack.push(
      id,
      hasBackdrop,
      coversPanel,
      stackingWidth && stackingSide ? { width: stackingWidth, side: stackingSide } : undefined,
    );
    return () => stack.pop(id);
  }, [open, id, stack, hasBackdrop, coversPanel, stackingWidth, stackingSide]);

  const isTopmost = useSyncExternalStore(
    stack.subscribe,
    () => stack.isTopmost(id),
    // Server snapshot is never read for real: every consumer bails out to
    // `null` before render on the server (no `document`), so this value only
    // has to typecheck.
    () => true,
  );
  const isFullyCovered = useSyncExternalStore(
    stack.subscribe,
    () => stack.isFullyCovered(id),
    () => false,
  );
  const isPanelHidden = useSyncExternalStore(
    stack.subscribe,
    () => stack.isPanelHidden(id),
    () => false,
  );

  const stackedWidth = useSyncExternalStore(
    stack.subscribe,
    () => stack.stackedWidth(id),
    () => null,
  );

  const isStackedUnder = useSyncExternalStore(
    stack.subscribe,
    () => stack.isStackedUnder(id),
    () => false,
  );

  return { isTopmost, isFullyCovered, isPanelHidden, stackedWidth, isStackedUnder };
}

// Module-level, deliberately outside the `DrawerStack` class: this answers a
// different question ("is ANY backdropped overlay open, anywhere") than the
// stack does ("which one is topmost"), and every `Drawer`/`Modal` in the app
// — regardless of which stack instance it registered with — has to agree on
// one shared count. A topmost overlay counts the same as a hidden/covered
// one: a hidden overlay is still logically open, and only the LAST one
// closing should unlock scroll.
let openOverlayCount = 0;

/**
 * Locks page scroll (via the `data-overlay-open` attribute `.scrollWrapper`
 * keys off of — see `Drawer`'s own comment on why body-scroll-locking is a
 * no-op in this app) for as long as at least one backdropped overlay
 * (`Drawer` or `Modal`) is open anywhere.
 *
 * Refcounted rather than a plain boolean set/cleared by each instance
 * independently: with nesting supported, the first of several open overlays
 * to close would otherwise delete the flag and unlock scroll while another
 * was still open.
 *
 * Not used by `SidePanelHost`: that aside never locked page scroll before
 * this stack existed (it covers only part of the screen and has no
 * backdrop), and extending that is a separate, unrequested behavior change —
 * left alone here.
 */
export function useOverlayScrollLock(open: boolean) {
  useEffect(() => {
    if (!open) return;
    openOverlayCount += 1;
    document.documentElement.dataset.overlayOpen = 'true';
    return () => {
      openOverlayCount = Math.max(0, openOverlayCount - 1);
      if (openOverlayCount === 0) {
        delete document.documentElement.dataset.overlayOpen;
      }
    };
  }, [open]);
}
